"use client";
import { useState, useEffect, useRef } from "react";
import { useCartContext } from "@/contexts/CartProvider";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import type { AbandonedCartTestResult } from "@/monarch/rules/abandonedCart/types";
import CartIcon from "./CartIcon";
import CartPopup from "./CartPopup";

interface AbandonedCartTestProps {
  className?: string;
}

export default function AbandonedCartTest({
  className,
}: AbandonedCartTestProps) {
  const [isOpen, setIsOpen] = useState(false);
  const cartRef = useRef<HTMLDivElement>(null);
  const {
    state: { cartData },
    shouldShowBadge,
  } = useCartContext();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        cartRef.current &&
        event.target &&
        !cartRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <MonarchDataConsumer<AbandonedCartTestResult>
      experimentKey="useAbandonedCart"
      render={(testResult) => {
        // If no test data or test is disabled, return null
        if (!testResult || !testResult.data?.enabled) {
          return null;
        }
        return (
          <div className="relative" ref={cartRef}>
            <CartIcon
              className={className}
              shouldShowBadge={shouldShowBadge}
              onClick={() => setIsOpen(!isOpen)}
              onMouseEnter={() => setIsOpen(true)}
            />

            <CartPopup
              isOpen={isOpen}
              onMouseEnter={() => setIsOpen(true)}
              onMouseLeave={() => setIsOpen(false)}
              cartData={cartData}
            />
          </div>
        );
      }}
    />
  );
}
